import { parseContact, type EventType, type ParsedContact } from '@aikoa/common'
import { supabase } from '~/lib/supabase'

const CONTACT_SELECT_FIELDS = `*,
      feedback (
        id,
        created_at,
        updated_at,
        send_at,
        sending_method,
        follow_up_enabled,
        declined_at,
        short_id,
        user_id,
        responder_user_id,
        completed_at,
        notifications:feedback_notifications(method, status, sent_at),
        interests(id, data, created_at)
      )`

export const CONTACTS_POPOVER_DEFAULT_PAGE_SIZE = 30
export const CONTACTS_POPOVER_SEARCH_CAP = 10
const CONTACTS_POPOVER_SEARCH_FETCH_LIMIT = 60

export type ContactsSortBy = 'created_at' | 'name' | 'updated_at'
export type SortDirection = 'asc' | 'desc'

type GetContactsPageInput = {
  offset: number
  pageSize?: number
  excludeContactIds?: string[]
}

type GetContactsPageForListingInput = {
  offset: number
  pageSize?: number
  sortBy?: ContactsSortBy
  sortDirection?: SortDirection
}

type ContactsPageResult = {
  contacts: ParsedContact[]
  nextOffset: number | null
}

type SearchContactsInput = {
  search: string
  excludeContactIds?: string[]
  fetchLimit?: number
}

function getInFilterValue(values: string[]) {
  const escapedValues = values.map((value) => `"${value.replaceAll('"', String.raw`\"`)}"`)
  return `(${escapedValues.join(',')})`
}

function getSafeOrSearchValue(value: string) {
  return value.replaceAll(',', ' ').replaceAll('%', '')
}

function parseContacts(rawContacts: unknown[]) {
  return rawContacts.map((rawContact) =>
    parseContact(rawContact as Parameters<typeof parseContact>[0])
  )
}

function mapContactsWithFeedback(contacts: ParsedContact[]) {
  return contacts.filter((contact) => !!contact.name && (!!contact.email || !!contact.phone))
}

export async function getContactInfo(contactId: string) {
  const { data, error } = await supabase
    .from('contacts')
    .select('name, email, phone, id, language')
    .eq('id', contactId)
    .maybeSingle()

  if (error || !data) {
    console.error(error || 'Contact not found')
    return null
  }

  return data
}

export async function getAllContacts({ assetId }: { assetId?: string }): Promise<ParsedContact[]> {
  void assetId

  const { data, error } = await supabase
    .from('contacts')
    .select(CONTACT_SELECT_FIELDS)
    .is('deleted_at', null)
    .is('feedback.deleted_at', null)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching all leads:', error)
    return []
  }

  if (!data) {
    return []
  }

  const filtered = data

  return mapContactsWithFeedback(parseContacts(filtered))
}

export async function getContactsPage({
  offset,
  pageSize = CONTACTS_POPOVER_DEFAULT_PAGE_SIZE,
  excludeContactIds,
}: GetContactsPageInput): Promise<ContactsPageResult> {
  const rangeStart = Math.max(offset, 0)
  const rangeEnd = rangeStart + pageSize - 1

  const contactsQuery = supabase
    .from('contacts')
    .select(CONTACT_SELECT_FIELDS)
    .is('deleted_at', null)
    .is('feedback.deleted_at', null)
    .order('updated_at', { ascending: false })
    .range(rangeStart, rangeEnd)

  if (excludeContactIds && excludeContactIds.length > 0) {
    contactsQuery.not('id', 'in', getInFilterValue(excludeContactIds))
  }

  const { data, error } = await contactsQuery

  if (error) {
    console.error('Error fetching paged contacts:', error)
    return {
      contacts: [],
      nextOffset: null,
    }
  }

  const rawContacts = data ?? []
  const parsedContacts = mapContactsWithFeedback(parseContacts(rawContacts))
  const hasMore = rawContacts.length === pageSize

  return {
    contacts: parsedContacts,
    nextOffset: hasMore ? rangeStart + pageSize : null,
  }
}

export async function searchContacts({
  search,
  excludeContactIds,
  fetchLimit = CONTACTS_POPOVER_SEARCH_FETCH_LIMIT,
}: SearchContactsInput): Promise<ParsedContact[]> {
  const trimmedSearch = search.trim()

  if (trimmedSearch.length === 0) {
    return []
  }

  const safeSearch = getSafeOrSearchValue(trimmedSearch)

  const contactsQuery = supabase
    .from('contacts')
    .select(CONTACT_SELECT_FIELDS)
    .is('deleted_at', null)
    .is('feedback.deleted_at', null)
    .or(`name.ilike.%${safeSearch}%,phone.ilike.%${safeSearch}%,email.ilike.%${safeSearch}%`)
    .order('updated_at', { ascending: false })
    .limit(fetchLimit)

  if (excludeContactIds && excludeContactIds.length > 0) {
    contactsQuery.not('id', 'in', getInFilterValue(excludeContactIds))
  }

  const { data, error } = await contactsQuery

  if (error) {
    console.error('Error searching contacts:', error)
    return []
  }

  return mapContactsWithFeedback(parseContacts(data ?? []))
}

export async function getContactsPageForListing({
  offset,
  pageSize = CONTACTS_POPOVER_DEFAULT_PAGE_SIZE,
  sortBy = 'created_at',
  sortDirection = 'desc',
}: GetContactsPageForListingInput): Promise<ContactsPageResult> {
  const rangeStart = Math.max(offset, 0)
  const rangeEnd = rangeStart + pageSize - 1

  const { data, error } = await supabase
    .from('contacts')
    .select(CONTACT_SELECT_FIELDS)
    .is('deleted_at', null)
    .is('feedback.deleted_at', null)
    .order(sortBy, { ascending: sortDirection === 'asc' })
    .range(rangeStart, rangeEnd)

  if (error) {
    console.error('Error fetching contacts for listing:', error)
    return { contacts: [], nextOffset: null }
  }

  const rawContacts = data ?? []
  const parsedContacts = parseContacts(rawContacts)
  const hasMore = rawContacts.length === pageSize

  return {
    contacts: parsedContacts,
    nextOffset: hasMore ? rangeStart + pageSize : null,
  }
}

export async function getContactsByIds(contactIds: string[]): Promise<ParsedContact[]> {
  if (contactIds.length === 0) {
    return []
  }

  const { data, error } = await supabase
    .from('contacts')
    .select(CONTACT_SELECT_FIELDS)
    .is('deleted_at', null)
    .is('feedback.deleted_at', null)
    .in('id', contactIds)

  if (error) {
    console.error('Error fetching contacts by ids:', error)
    return []
  }

  const contactsById = new Map(
    mapContactsWithFeedback(parseContacts(data ?? [])).map(
      (contact) => [contact.id, contact] as const
    )
  )

  const orderedContacts: ParsedContact[] = []

  for (const contactId of contactIds) {
    const contact = contactsById.get(contactId)

    if (contact) {
      orderedContacts.push(contact)
    }
  }

  return orderedContacts
}

export async function getHasAnyContactsForFeedback(): Promise<boolean> {
  const firstPage = await getContactsPage({
    offset: 0,
    pageSize: 1,
  })

  return firstPage.contacts.length > 0
}

export async function getAssetEventContacts(assetId: string, eventType: EventType) {
  const { data, error } = await supabase
    .from('event_contacts')
    .select(
      `
      contact:contacts!inner(
        id,
        name,
        email,
        phone,
        language
      ),
      event:events!inner(
        id,
        asset_id,
        event_type
      )
    `
    )
    .eq('event.asset_id', assetId)
    .eq('event.event_type', eventType)
    .is('contact.deleted_at', null)
    .limit(1)
    .maybeSingle()

  if (error) {
    console.error('Error fetching asset event contacts:', error)
    return null
  }

  return data?.contact ?? null
}
